/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { StatusBadge, PriorityBadge } from "../components/StatusBadge";
import { Camera, Trash2 } from "lucide-react";
import { PhotoStrip } from "../components/PhotoStrip";
import PhotoUploadButton from "../components/PhotoUploadButton";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import PeopleTagger, { PeoplePills } from "../components/PeopleTagger";

// DEMO placeholder — actual match id now comes from useMatch() at runtime
const PRIOS = ["critical", "high", "medium", "low"];

export default function VenueReadinessPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;
  const { user: me } = useAuth();

  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState({});
  const [preMatchDepts, setPreMatchDepts] = useState([]);
  const [form, setForm] = useState({
    area: "", sub_area: "", observation: "", category: "General",
    priority: "medium", status: "assigned", responsible_dept: "", required_action: "",
    tagged_people: [],
  });
  const [showNew, setShowNew] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    if (!DEMO) return;
    const [items, locs, depts] = await Promise.all([
      api.get(`/matches/${DEMO}/venue-items`),
      api.get(`/reference/locations`),
      api.get(`/reference/pre-match-departments?match_id=${DEMO}`),
    ]);
    setItems(items.data);
    setLocations(locs.data.locations);
    setPreMatchDepts(depts.data.departments);
    // seed form defaults from reference data once loaded
    setForm((f) => {
      const areaKeys = Object.keys(locs.data.locations);
      const nextArea = f.area || areaKeys[0] || "";
      const nextSub = f.sub_area || (locs.data.locations[nextArea]?.[0] ?? "");
      const firstDept = [...depts.data.departments].sort((a, b) => a.localeCompare(b))[0] || "";
      const nextDept = f.responsible_dept || firstDept;
      return { ...f, area: nextArea, sub_area: nextSub, responsible_dept: nextDept };
    });
  };
  useEffect(() => { load(); }, [DEMO]); // eslint-disable-line react-hooks/exhaustive-deps

  // When area changes, reset sub_area to first option under it
  useEffect(() => {
    if (form.area && locations[form.area]) {
      if (!locations[form.area].includes(form.sub_area)) {
        setForm((f) => ({ ...f, sub_area: locations[form.area][0] }));
      }
    }
  }, [form.area, locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const markResolved = async (id) => {
    // "Ready" is the terminal / resolved state in the venue-item workflow
    await api.patch(`/venue-items/${id}`, { status: "ready", required_action: "Resolved" });
    load();
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this inspection item?")) return;
    await api.delete(`/venue-items/${id}`);
    load();
  };

  const uploadPhoto = async (itemId, file) => {
    setUploading(itemId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/venue-items/${itemId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally {
      setUploading(null);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    // Always create with status = "assigned" (fixed by design); tag the current user as the assignor.
    await api.post(`/matches/${DEMO}/venue-items`, {
      ...form,
      status: "assigned",
      inspector: me?.name || me?.email || "",
    });
    setForm({ ...form, observation: "", required_action: "", tagged_people: [] });
    setShowNew(false);
    load();
  };

  const grouped = items.reduce((acc, i) => {
    (acc[i.area] = acc[i.area] || []).push(i);
    return acc;
  }, {});

  const sortedDepts = [...preMatchDepts].sort((a, b) => a.localeCompare(b));

  if (!selectedMatchId) return <RequireMatch />;
  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-venue">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Inspection · Areas · Assets</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Issues Post Inspection</h1>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          data-testid="venue-new-btn"
          className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-zinc-200"
        >
          {showNew ? "Cancel" : "+ New Inspection Item"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="venue-form">
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.area}
            onChange={(e) => setForm({ ...form, area: e.target.value })}
            required
            data-testid="venue-form-area"
          >
            {Object.keys(locations).map((a) => <option key={a}>{a}</option>)}
          </select>
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.sub_area}
            onChange={(e) => setForm({ ...form, sub_area: e.target.value })}
            data-testid="venue-form-subarea"
          >
            {(locations[form.area] || []).map((s) => <option key={s}>{s}</option>)}
          </select>
          <input className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2" placeholder="Observation" required value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })} data-testid="venue-form-obs" />
          <select className="bg-black border border-cmdBorder p-2 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {PRIOS.map((p) => <option key={p}>{p}</option>)}
          </select>
          <div className="bg-black border border-cmdBorder p-2 text-sm flex items-center gap-2" data-testid="venue-form-status-fixed">
            <span className="mc-label">Assigned to</span>
            <span className="w-2 h-2 rounded-full bg-warnAmber" />
          </div>
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.responsible_dept}
            onChange={(e) => setForm({ ...form, responsible_dept: e.target.value })}
            data-testid="venue-form-dept"
            required
          >
            {sortedDepts.map((d) => <option key={d}>{d}</option>)}
          </select>
          <button type="submit" className="bg-readyGreen text-black font-bold uppercase tracking-widest text-xs" data-testid="venue-form-submit">Create</button>
          <div className="md:col-span-4">
            <label className="mc-label">Tag Individuals (optional)</label>
            <div className="mt-1">
              <PeopleTagger
                value={form.tagged_people}
                onChange={(v) => setForm({ ...form, tagged_people: v })}
                placeholder="Add names of inspectors / assignees…"
                testidPrefix="venue-tagger"
              />
            </div>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {Object.entries(grouped).map(([area, list]) => (
          <div key={area} className="mc-panel" data-testid={`venue-area-${area}`}>
            <div className="flex items-center justify-between p-4 border-b border-cmdBorder">
              <h3 className="font-head font-bold text-xl uppercase">{area}</h3>
              <div className="mc-label">{list.length} items</div>
            </div>
            <div className="divide-y divide-cmdBorder">
              {list.map((it) => (
                <div key={it.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`venue-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{it.observation}</div>
                    <div className="text-xs text-zinc-500 mt-1">{it.sub_area} · {it.category}</div>
                    <div className="text-xs text-zinc-400 mt-1" data-testid={`venue-assignment-${it.id}`}>
                      Assigned by <span className="text-white">{it.inspector || "—"}</span> to <span className="text-white">{it.responsible_dept || "—"}</span>
                      {it.tagged_people?.length > 0 && (
                        <>
                          <span className="text-zinc-600 mx-1">·</span>
                          <PeoplePills people={it.tagged_people} testidPrefix={`venue-tagged-${it.id}`} />
                        </>
                      )}
                    </div>
                    {it.required_action && <div className="text-xs text-warnAmber mt-1 border-l-2 border-warnAmber pl-2">{it.required_action}</div>}
                    <PhotoStrip paths={it.photos} testidPrefix={`venue-photos-${it.id}`} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:justify-end shrink-0">
                    <PriorityBadge priority={it.priority} done={it.status === "resolved"} />
                    <StatusBadge status={it.status} kind="venue" live={["issue_identified", "assigned"].includes(it.status)} />
                    <input
                      type="file"
                      accept="image/*"
                      ref={(el) => (fileRefs.current[it.id] = el)}
                      onChange={(e) => e.target.files?.[0] && uploadPhoto(it.id, e.target.files[0])}
                      className="hidden"
                      data-testid={`venue-file-${it.id}`}
                    />
                    <PhotoUploadButton
                      onUpload={(file) => uploadPhoto(it.id, file)}
                      label={`Photo (${it.photos?.length || 0})`}
                      testid={`venue-upload-${it.id}`}
                    />
                    {it.status !== "ready" && (
                      <button
                        onClick={() => markResolved(it.id)}
                        className="whitespace-nowrap border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-black px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                        data-testid={`venue-resolve-${it.id}`}
                      >
                        Mark Resolved
                      </button>
                    )}
                    <button
                      onClick={() => removeItem(it.id)}
                      className="flex items-center gap-1 border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                      data-testid={`venue-delete-${it.id}`}
                      title="Delete inspection item"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
